import { Box } from "@mui-wrapper";
import { Text, TextVariant, NO_TRANSLATE_CLASS } from "@flat-ui";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import { useId, type ReactNode } from "react";

/** A single labelled item (label above value) in the info bar. */
interface InfoBarItemConfig {
  /** Small label shown above the value */
  label: string;

  /** The value. Plain strings are rendered in the standard value style */
  content: ReactNode;

  /** Whether the item should be hidden. Hidden items don't take up space in the layout. */
  isHidden?: boolean;
}

interface Props {
  /** The labelled items, spread evenly across the bar */
  items: InfoBarItemConfig[];

  /** Optional action, e.g. buttons, shown at the far right of the bar */
  action?: React.ReactNode;

  /**
   * Whether the room for the action is kept even while there is none. A page whose tabs show an
   * action only on some of them needs it, or the items would move whenever the action comes and goes.
   */
  shouldReserveActionSpace?: boolean;
}

/** Room kept for the action: the widest one, Add Feature and Add Subscription side by side */
const ACTION_SLOT_WIDTH = "26rem";

/** Width an item can shrink to before it wraps onto the next row of the bar */
const ITEM_MIN_WIDTH = "12rem";

/** Height of the bar, so all info bars line up across pages */
const BAR_MIN_HEIGHT = "4.5rem";

/**
 * A grey bar with a few labelled values about the current page, as the Dashboard shows them
 * under its tabs. The items share the bar equally and cut a long value off with an ellipsis, and
 * the action sits at the far right.
 */
export function InfoBar({
  items,
  action,
  shouldReserveActionSpace = false,
}: Props): React.JSX.Element {
  const idPrefix = useId();

  return (
    <Box
      sx={{
        minHeight: BAR_MIN_HEIGHT,
        py: 1,
        backgroundColor: "surfaces.secondary",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        rowGap: 1,
        // Clips the divider of the item that starts a row
        overflow: "hidden",
      }}
    >
      {items
        .filter(({ isHidden }) => !isHidden)
        .map(({ label, content }, index) => {
          // Not from the label: ids can't hold spaces, which would split the reference in two
          const labelId = `${idPrefix}-${index}`;

          return (
            <Box
              key={label}
              role="group"
              aria-labelledby={labelId}
              sx={{
                px: 2.5,
                // Every item takes an equal share of the bar, so a short value still gets a wide one
                flex: `1 1 ${ITEM_MIN_WIDTH}`,
                minWidth: 0,
                // Every item draws its divider 1px outside its left edge, which the bar clips for the
                // item that starts a row, so dividers only ever sit between two items on the same row.
                borderLeft: 1,
                borderColor: "divider",
                ml: "-1px",
                "&:first-of-type": { borderLeft: "none" },
              }}
            >
              <Text
                id={labelId}
                variant="helpText"
                shouldElide
                sx={{ display: "block" }}
              >
                {label}
              </Text>

              {typeof content === "string" ? (
                <Box className={NO_TRANSLATE_CLASS} sx={{ minWidth: 0 }}>
                  <TextWithTooltip
                    text={content}
                    textVariant={TextVariant.heading16}
                  />
                </Box>
              ) : (
                content
              )}
            </Box>
          );
        })}

      {(action || shouldReserveActionSpace) && (
        <Box
          sx={{
            ml: "auto",
            px: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            flex: shouldReserveActionSpace
              ? `0 0 ${ACTION_SLOT_WIDTH}`
              : undefined,
          }}
        >
          {action}
        </Box>
      )}
    </Box>
  );
}
